import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useAllLaudos, useLaudos } from "@/hooks/use-data";
import { useLocation } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { FileText, Users, Search, ArrowLeft, Building2 } from "lucide-react";
import { LAUDO_STATUS_LABELS, LAUDO_STATUS_COLORS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useState, useMemo } from "react";
import { LaudoWithDetails } from "@shared/schema";
import { LaudoCard } from "./Laudos";

interface Grupo {
  parceiroId: string;
  nome: string;
  laudos: LaudoWithDetails[];
}

export default function LaudosPorParceiro() {
  const [busca, setBusca] = useState("");
  const [selectedParceiro, setSelectedParceiro] = useState<Grupo | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [, navigate] = useLocation();

  const { profile } = useAuth();
  const isOwner = profile?.roles?.includes("owner") || false;
  const isParceiro = profile?.roles?.includes("parceiro_negocios") || false;
  const empresaId = profile?.empresa?.id;
  const vistoriadoraId = isOwner || isParceiro ? undefined : empresaId;
  const parceiroId = isParceiro ? empresaId : undefined;

  const { data: laudos, isLoading } = isOwner
    ? useAllLaudos()
    : useLaudos(vistoriadoraId, undefined, undefined, undefined, parceiroId);

  const grupos = useMemo<Grupo[]>(() => {
    if (!laudos) return [];
    const map = new Map<string, Grupo>();
    for (const l of laudos) {
      const pid = l.parceiroId ?? "sem_parceiro";
      const nome =
        (l as any).parceiro?.nomeFantasia ||
        (l as any).parceiro?.razaoSocial ||
        "Sem parceiro";
      if (!map.has(pid)) map.set(pid, { parceiroId: pid, nome, laudos: [] });
      map.get(pid)!.laudos.push(l);
    }
    return Array.from(map.values());
  }, [laudos]);

  const gruposFiltrados = useMemo(() =>
    grupos.filter((g) => g.nome.toLowerCase().includes(busca.toLowerCase())),
    [grupos, busca],
  );

  const laudosFiltrados = useMemo(() => {
    if (!selectedParceiro) return [];
    if (statusFilter === "all") return selectedParceiro.laudos;
    return selectedParceiro.laudos.filter((l) => l.status === statusFilter);
  }, [selectedParceiro, statusFilter]);

  // ── Tela de detalhe: laudos do parceiro ──────────────────────────────────
  if (selectedParceiro) {
    return (
      <Shell>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => { setSelectedParceiro(null); setStatusFilter("all"); }}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-display text-slate-900">
                {selectedParceiro.nome}
              </h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                {selectedParceiro.laudos.length} laudo{selectedParceiro.laudos.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Status</SelectItem>
              {Object.entries(LAUDO_STATUS_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {laudosFiltrados.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <FileText className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">Nenhum laudo encontrado.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {laudosFiltrados.map((laudo) => (
              <LaudoCard
                key={laudo.id}
                laudo={laudo}
                canSelectTecnico={false}
                tecnicos={[]}
                editingLaudoId={null}
                onSelectTecnico={() => {}}
                onNavigate={() => navigate(`/laudos/${laudo.id}`)}
              />
            ))}
          </div>
        )}
      </Shell>
    );
  }

  // ── Tela principal: cards dos parceiros ──────────────────────────────────
  return (
    <Shell>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold font-display text-slate-900">
            Laudos por Parceiro
          </h1>
          <p className="text-muted-foreground mt-1">
            Selecione um parceiro para ver seus laudos
          </p>
        </div>
        <div className="relative w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar parceiro..."
            className="pl-9"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      ) : gruposFiltrados.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <Users className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-sm">Nenhum parceiro encontrado.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {gruposFiltrados.map((grupo) => {
            const porStatus = grupo.laudos.reduce<Record<string, number>>((acc, l) => {
              acc[l.status] = (acc[l.status] ?? 0) + 1;
              return acc;
            }, {});

            return (
              <Card
                key={grupo.parceiroId}
                className="border-none shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedParceiro(grupo)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{grupo.nome}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {grupo.laudos.length} laudo{grupo.laudos.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(porStatus).map(([status, count]) => (
                      <Badge
                        key={status}
                        variant="secondary"
                        className={cn("text-xs", LAUDO_STATUS_COLORS[status])}
                      >
                        {LAUDO_STATUS_LABELS[status] ?? status} · {count}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </Shell>
  );
}
